import {
  AccessContext,
  DateRangeField,
  DateTimeField,
  DynamicColumn,
  ForeignKeyField
} from "lego-gui-lib";
import { EnumField, Required, Schema, TextField } from "lego-gui-lib/lib";
import { NotApplicable } from "lego-gui-lib/lib/components/Inputs/NotApplicable";
import { createContext } from "react";
import { api, mainStore } from "../../core/mainStore";
import { CustomerFK } from "../customer/schema";
import { t } from "../../core/i18n/i18n";
import { SANFK } from "../san/schema";
import { RemoteChildDataSource } from "lego-gui-lib/lib/dataAccess/RemoteChildDataSource";
import {
  queryParamDtoStrategy,
  queryParamParentChangeStrategy
} from "lego-gui-lib/lib/dataAccess/ChildDataSourceStrategies";
import { Auth } from "../../core/model/auth";
import { TreeDataSource } from "./TreeDataSource";
import { BooleanField } from "../../common/common";

export enum TreeType {
  MAIN_TREE,
  SUB_TREE
}

export enum TreeStatus {
  INACTIVE,
  ACTIVE
}

export const TreeTypes = [
  { label: t("hns:tree:treeType.main"), value: TreeType.MAIN_TREE },
  { label: t("hns:tree:treeType.sub"), value: TreeType.SUB_TREE }
];

export const StatusTypes = [
  { label: t("hns:tree:status:inactive"), value: TreeStatus.INACTIVE },
  { label: t("hns:tree:status:active"), value: TreeStatus.ACTIVE }
];

export const TreeSchema = new Schema<DynamicColumn>({
  primaryKey: ["id"],
  context: { auth: mainStore.auth },
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  fields: [
    {
      name: "customerRef",
      type: CustomerFK,
      sort: { field: "name" },
      title: t("hns:tree:customerRef")
    },
    {
      name: "sanRef",
      type: SANFK,
      sort: { field: "name" },
      title: t("hns:tree:sanRef")
    },
    {
      name: "name",
      type: TextField(Required),
      title: t("common:name")
    },
    {
      name: "type",
      type: EnumField(TreeTypes, Required),
      readonly: true,
      title: t("common:type"),
      onChange: (nv, ov, formState) => {
        formState.values.activationTime = undefined;
      }
    },
    {
      name: "isComplete",
      type: BooleanField(),
      readonly: true,
      title: t("hns:tree:isComplete")
    },
    {
      name: "status",
      type: EnumField(StatusTypes, Required),
      readonly: true,
      title: t("common:status")
    },
    {
      name: "activationTime",
      // @ts-ignore
      type: DateTimeField({ useSimplePicker: true, clearable: true }),
      title: t("hns:tree:activationTime")
    }
  ]
});

export const TreeSchemaColumns = TreeSchema.extend({
  customerRef: false,
  sanRef: false,
  // @ts-ignore
  type: { type: EnumField(TreeTypes, true, Required) },
  status: { type: EnumField(StatusTypes, Required) },
  activationTime: {
    // @ts-ignore
    type: NotApplicable(
      DateRangeField(),
      (ctx: AccessContext) => ctx.data && ctx.data.type === TreeType.SUB_TREE
    )
  }
});

export const TreeCreateSchema = TreeSchema.extend({
  customerRef: false,
  sanRef: {
    name: "sanRef",
    type: SANFK,
    if: () => false
  },
  type: { type: EnumField(TreeTypes, Required), readonly: false },
  isComplete: false,
  status: false,
  activationTime: {
    if: (ctx: AccessContext) => ctx.data && ctx.data.type === TreeType.MAIN_TREE
  }
});

export const TreeShowSchema = TreeSchema.extend({
  sanRef: false,
  customerRef: false,
  status: {
    type: EnumField(StatusTypes, Required),
    if: (ctx: AccessContext) => ctx.data && ctx.data.type === TreeType.MAIN_TREE
  },
  activationTime: {
    if: (ctx: AccessContext) => ctx.data && ctx.data.type === TreeType.MAIN_TREE
  }
});

mainStore.tree = new TreeDataSource({
  parent: mainStore.SAN.oneDataSource,
  parentChangedStrategy: queryParamParentChangeStrategy("id", "sanRef"),
  dtoStrategy: queryParamDtoStrategy("id", "sanRef"),
  api: api,
  name: "tree",
  schema: TreeSchema,
  url: "/trees",
  commons: mainStore.commons
});

export const treeContext = createContext(mainStore.tree);

mainStore.treeFk = RemoteChildDataSource.clone(mainStore.tree);
mainStore.subTreeFk = RemoteChildDataSource.clone(mainStore.tree, {
  params: {
    filters: [
      {
        field: "type",
        value: TreeType.SUB_TREE.toString()
      },
      {
        field: "isComplete",
        value: "1"
      }
    ]
  }
});

export const TreeFK = ForeignKeyField(
  {
    dataSource: mainStore.treeFk.listDataSource,
    labelField: "name",
    valueField: "id",
    minWidth: 70
  },
  Required
);
export const TreeFKNotRequired = ForeignKeyField({
  dataSource: mainStore.treeFk.listDataSource,
  labelField: "name",
  valueField: "id",
  minWidth: 70
});

export const SubTreeFK = ForeignKeyField(
  {
    dataSource: mainStore.subTreeFk.listDataSource,
    labelField: "name",
    valueField: "id",
    minWidth: 70
  },
  Required
);
